import { useMemo } from 'react';
import { useQuery } from '@tanstack/react-query';
import { supabase } from './supabase';
import { getTodaySessionDate } from '../utils/schedule';
import {
  isSessionDate,
  nextSessionDateAcross,
  NON_TEACHING_TYPES_FILTER,
} from './programSessions';
import { sampleProgramsForToday } from './sample/sampleData';
import { PROGRAM_LOCATION_EMBED, type WithProgramLocation } from '../types/location';

export interface TodayProgram extends WithProgramLocation {
  id: string;
  name: string;
  type: string;
  schedule_cron: string;
  billing_type: string;
  start_date: string;
  end_date: string;
  /** Minutes the session runs for. With the cron's start time, this is the class window. */
  duration: number;
  /** Per-program no-class dates; `isSessionDate` treats them as non-meeting days. */
  excluded_dates: string[] | null;
}

interface StaffUserScope {
  program_ids: string[] | null;
  /**
   * Virtual classes this teacher holds. Unlike `program_ids`, `null` means *none* — they
   * are an explicit grant (see the column comment in the DB).
   */
  virtual_class_keys?: string[] | null;
}

const PROGRAM_COLUMNS =
  `id, name, type, schedule_cron, billing_type, start_date, end_date, duration, excluded_dates, brand_key, ${PROGRAM_LOCATION_EMBED}`;

/**
 * Every active after-school program, whatever day it is. Filtered to a date by the callers
 * below, so moving the roster's day forward is a re-filter rather than a refetch.
 */
function useAfterSchoolPrograms(enabled: boolean) {
  return useQuery<TodayProgram[]>({
    queryKey: ['staff-after-school-programs'],
    queryFn: async () => {
      const { data, error } = await supabase
        .from('programs')
        .select(PROGRAM_COLUMNS)
        .eq('active', true)
        .eq('type', 'after-school')
        .order('start_date');
      if (error) throw error;
      return (data ?? []) as TodayProgram[];
    },
    enabled,
  });
}

/**
 * The after-school programs meeting on `date`, regardless of what this teacher is scoped to.
 *
 * Virtual classes hang off the after-school session: it supplies their calendar (term
 * dates, weekdays, closures), the `program_id` their attendance is written against, and
 * each child's billing type. A teacher who holds *only* Chinese G3 is scoped to no
 * programs at all, so without this their subject tab could never be built.
 *
 * Deliberately separate from `useTodaysPrograms` rather than widening it. These programs
 * are seats, not rosters — folding them into the scoped list would hand that same teacher
 * an After School tab they have no business opening.
 *
 * `date` is the roster's session date from `useTodaysPrograms` — today, or the next
 * session when nothing meets today — so the seats always match the day on screen.
 */
export function useAfterSchoolProgramsOn(enabled: boolean, date: string) {
  const query = useAfterSchoolPrograms(enabled);
  const data = useMemo(
    () => query.data?.filter(p => isSessionDate(p, date)),
    [query.data, date],
  );
  return { ...query, data };
}

/**
 * Which day the roster is for: today when any of the teacher's programs or seats meets
 * today, otherwise the next day one does (`isPreview`). With nothing coming up either, it
 * stays on today — the caller's sample fallback, or empty state, takes it from there.
 *
 * Exported for testing.
 */
export function resolveRosterDay(
  programs: readonly TodayProgram[],
  seats: readonly TodayProgram[],
  today: string,
): { sessionDate: string; isPreview: boolean } {
  const all = [...programs, ...seats];
  if (all.some(p => isSessionDate(p, today))) return { sessionDate: today, isPreview: false };
  const next = nextSessionDateAcross(all, today);
  return next ? { sessionDate: next, isPreview: true } : { sessionDate: today, isPreview: false };
}

/**
 * The teacher's programs for the roster day — normally TODAY.
 *
 * The portal is a day-of tool: a teacher opens it at pickup time to check students in and
 * out. A program that does not meet on the roster day has nothing to record, so it is
 * filtered out entirely rather than shown in a disabled state — every program the UI
 * offers is one you can act on (or, in preview, one that is about to meet).
 *
 * "Meets" means inside the date range, on a cron weekday, and not a school closure (see
 * `isSessionDate`).
 *
 * When nothing meets today, the roster day moves forward to the next day any of the
 * teacher's rosters meets — a weekend opens on Monday, a Wednesday-only chess teacher sees
 * Wednesday's class. `isPreview` marks that case: `sessionDate` is then in the future, and
 * every screen must render read-only and write nothing (no seeded attendance rows for a day
 * that has not happened).
 *
 * Only when there is no upcoming session either (a long break, or no programs at all) does
 * it fall back to a made-up class. `isSample` says so;
 * every screen that renders sample programs must watermark itself (see `SampleModeBanner`).
 *
 * "Nothing to show" is deliberately NOT the same question as "this query came back empty".
 * A teacher who holds only Chinese or Math is scoped to no programs at all, so an empty
 * result is the correct answer for them — their roster hangs off the after-school session
 * instead (see `useAfterSchoolProgramsOn`), and the after-school calendar is what decides
 * their next session too. Firing the sample for them cost more than a misleading screen:
 * the sample after-school program became the seats behind their subject tab, so the roster
 * listed real children while the attendance query asked for `sample-program-after-school`
 * and matched none of the rows it had just seeded — leaving every name on screen with no
 * record, and therefore no check-in or check-out control.
 */
export function useTodaysPrograms(staffUser: StaffUserScope) {
  const scopedIds = staffUser.program_ids;
  const today = getTodaySessionDate();
  const holdsVirtual = (staffUser.virtual_class_keys?.length ?? 0) > 0;
  const seatsQuery = useAfterSchoolPrograms(holdsVirtual);

  // Keyed without the date: the list is the same all week, and the day is applied below.
  const query = useQuery<TodayProgram[]>({
    queryKey: ['staff-todays-programs', scopedIds],
    queryFn: async () => {
      let q = supabase
        .from('programs')
        .select(PROGRAM_COLUMNS)
        .eq('active', true)
        .not('type', 'in', NON_TEACHING_TYPES_FILTER);
      if (scopedIds) q = q.in('id', scopedIds);
      const { data, error } = await q.order('start_date');
      if (error) throw error;
      return (data ?? []) as TodayProgram[];
    },
  });

  // A virtual-class teacher's rosters are still pending while their seats load, so nothing
  // is decided until that query has answered — otherwise the sample (or a preview day)
  // flashes on and then off on every load, seeding the caches with the wrong ids in between.
  const seatsSettled = !holdsVirtual || seatsQuery.isSuccess;

  const resolved = useMemo(
    () =>
      query.isSuccess && seatsSettled
        ? resolveRosterDay(query.data ?? [], holdsVirtual ? seatsQuery.data ?? [] : [], today)
        : { sessionDate: today, isPreview: false },
    [query.data, query.isSuccess, seatsQuery.data, seatsSettled, holdsVirtual, today],
  );

  const { sessionDate, isPreview } = resolved;

  const onDay = useMemo(
    () => query.data?.filter(p => isSessionDate(p, sessionDate)),
    [query.data, sessionDate],
  );

  // Only a *successful* load with nothing today and nothing coming up substitutes the
  // sample: a failed load is a real failure and must keep behaving like one rather than
  // quietly showing fiction.
  const seatsOnDay = holdsVirtual
    ? (seatsQuery.data ?? []).some(p => isSessionDate(p, sessionDate))
    : false;
  const isSample =
    query.isSuccess &&
    seatsSettled &&
    (onDay?.length ?? 0) === 0 &&
    !seatsOnDay;

  return {
    ...query,
    data: isSample ? sampleProgramsForToday(today) : onDay,
    // The seats decide whether this teacher has a roster at all, so a caller that renders
    // an empty state off `isLoading` must wait for them too.
    isLoading: query.isLoading || (holdsVirtual && seatsQuery.isLoading),
    isSample,
    /** The real calendar day. */
    today,
    /**
     * The day the roster is for: `today`, or — when nothing meets today — the next session.
     * The sample always runs on `today`.
     */
    sessionDate: isSample ? today : sessionDate,
    /** True when `sessionDate` is a future day: read-only, and nothing may be written. */
    isPreview: isPreview && !isSample,
  };
}
